/** @jsx jsx */
import { Link } from 'gatsby';
import React, { useCallback } from 'react';
import { Box, Flex, jsx } from 'theme-ui';
import Button from './Button';

const isMhCom = process.env.GATSBY_SITE_DOMAIN === 'com';

const LanguageLink = ({ to, linkText, locale, active, paths, darkMode, color, ...props }) => {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <Button
        variant="plain"
        sx={{
          position: 'relative',
          fontWeight: active ? 'medium' : 'normal',
          px: 2,
          py: 0,
          color: ['white', null, null, color || 'primary'],
          ':focus:not(:active)': {
            boxShadow: 'inputFocus',
          },
          '::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 1,
            bg: 'border',
          },
          ':last-of-type::after': {
            display: 'none',
          },
        }}
        {...props}
      >
        {linkText}
      </Button>
    </Link>
  );
};

const LanguageDropdown = ({ path, paths, hideLocales = [], darkMode, color, ...props }) => {
  const [open, setOpen] = React.useState(false);
  const isComSe = (isMhCom && path && (path?.startsWith('/se/') || path === '/se')) || false;

  return (
    <div
      {...props}
      sx={{ position: 'relative', ...props.sx }}
      onMouseOver={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <DropdownToggle color={color} active={open} tabIndex="0" theme={props.theme} darkMode={props?.darkMode}>
        <Box
          sx={{
            display: ['none', 'block'],
            color,
            '::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 2,
              width: 18,
              height: '2px',
              backgroundColor: isComSe ? color : 'transparent',
            },
          }}
        >
          SE
        </Box>
      </DropdownToggle>
      {open && (
        <DropdownMenu
          open={open}
          onMouseLeave={() => {
            setOpen(false);
          }}
        >
          <DropdownMenuItem>
            <DropdownMenuItem>
              <LanguageLink
                key={'sv'}
                color={color}
                to={'/se'}
                darkMode={darkMode}
                paths={paths}
                locale={'sv'}
                linkText={'svenska'}
                className="language"
                role="button"
                aria-label={'svenska'}
              />
            </DropdownMenuItem>
            <LanguageLink
              key={'en'}
              color={color}
              darkMode={darkMode}
              paths={paths}
              locale={'en'}
              to={'/se/en'}
              linkText={'english'}
              className="language"
              role="button"
              aria-label={'english'}
            />
          </DropdownMenuItem>
        </DropdownMenu>
      )}
    </div>
  );
};

const DropdownToggle = (props) => {
  return (
    <Flex
      {...props}
      sx={{
        cursor: 'pointer',
        alignItems: 'center',
        color: props?.color,
        mx: 2,
        transition: 'color .25s ease',
        ':hover': {
          color: 'headerHover',
        },

        'svg:last-of-type': {
          transition: 'transform .25s ease',
          transform: props.active ? 'rotate(180deg)' : 'none',
        },
      }}
    >
      {props.children}
    </Flex>
  );
};

const DropdownMenu = (props) => (
  <Box
    sx={{
      position: 'absolute',
      top: '100%',
      right: 0,
      py: 2,
      bottom: 'auto',
      borderRadius: 1,
      boxShadow: 'dropdown',
      textAlign: 'right',
    }}
    {...props}
  >
    {props.children}
  </Box>
);

const DropdownMenuItem = React.forwardRef(({ children, onClick, ...props }, ref) => {
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      onClick();
    }
  }, []);
  return (
    <Box
      ref={ref}
      tabIndex="0"
      onClick={onClick}
      onKeyDown={onKeyDown}
      {...props}
      sx={{
        cursor: 'pointer',
        pb: 1,
        whiteSpace: 'nowrap',
        color: 'secondary',
      }}
    >
      {children}
    </Box>
  );
});

export default LanguageDropdown;
